// /**
//  * Driver - list
//  */

// React
import React, {useEffect, useContext} from 'react';

// Context
import AuthContext from '../../authContext';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiPencilOutline,
  mdiCarShiftPattern,
  mdiChevronDown,
  mdiChevronUp,
  mdiTextBoxMultipleOutline,
  mdiClose,
  mdiCheck,
  mdiHistory,
  mdiCreditCard,
  mdiNewspaperVariantMultipleOutline,
  mdiCurrencyUsd,
} from '@mdi/js';

// Helper
import {
  formaterPhoneNumber,
  translateEmploymentType,
  translateSettlementType,
  translateVehicleType,
} from '../../utils/helpers';

// Type
import {Driver, Source, DriverWallet} from '../drivers/interfaces';
import theme from '../../theme';
import {Link} from 'react-router-dom';

type Data = DriverWallet;
type Props = {
  data: any;
  fleetId: number;
  sourcesList: Source[];
  preferredSourcesLoading: boolean;
  preferredSources: any;
  handleConfirmUpdate: (body: Driver) => void;
  handleOpenShiftDialog: (body: Driver) => void;
  handleOpenSettleDialog: (body: DriverWallet) => void;
  handleOpenDetailPage: (id: number) => void;
  updateVehicleSource: (payload: {id: number; body: number[]}) => void;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  id?: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    dot: {
      position: 'relative',
      display: 'inline-block',
      top: '0',
      left: '-8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

// Const
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

const headCells: HeadCell[] = [
  {id: 'driverName', label: 'نام راننده'},
  {
    id: 'id',
    label: 'شناسه راننده',
  },
  {id: 'vehicleType', label: 'نوع خودرو'},
  {id: 'driverEmploymentType', label: 'نوع قرارداد'},
  {id: 'settlementType', label: 'نوع تسویه'},
  {id: 'amount', label: 'موجودی (ریال)'},
];

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <StyledTableCell />
        {headCells.map((headCell, index) => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={index}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={headCell.id && createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                {headCell.label}
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);

  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('id');
  const [dense] = React.useState(false);
  const [checkboxValue, setCheckboxValue] = React.useState([] as any);

  useEffect(() => {
    setCheckboxValue(props.preferredSources);
  }, [props.preferredSources]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleChange = async (event: React.ChangeEvent<{value: unknown}>) => {
    await setCheckboxValue(event.target.value);
  };
  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />

          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                const updateStatus =
                  row.employmentStatus === 'ACTIVE' ? 'OFF' : 'ACTIVE';

                return (
                  <TableRow key={index}>
                    <TableCell align="center">
                      {' '}
                      <span
                        className={classes.dot}
                        style={{
                          backgroundColor:
                            row.employmentStatus === 'ACTIVE'
                              ? theme.palette.green.main
                              : theme.palette.error.main,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">{row.driverName}</TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.driverId}
                    </TableCell>
                    <TableCell align="center">
                      {translateVehicleType(row.vehicleType)}
                    </TableCell>
                    <TableCell align="center">
                      {translateEmploymentType(row.driverEmploymentType)}
                    </TableCell>
                    <TableCell align="center">
                      {translateSettlementType(row.settlementType)}
                    </TableCell>
                    <TableCell align="center">{new Intl.NumberFormat('fa-IR').format(row.amount)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="details"
                        size="small"
                        title="جزئیات راننده"
                        data-cy="details"
                        onClick={() => props.handleOpenDetailPage(row.id)}
                      >
                        <Icon
                          path={mdiTextBoxMultipleOutline}
                          size={1}
                          color={theme.palette.green.main}
                        />
                      </IconButton>
                      <Link to={`/drivers-wallet/${row.driverId}/charge`}>
                        <IconButton title="شارژ کیف پول" size="small">
                          <Icon path={mdiCreditCard} color="#111111" size={1} />
                        </IconButton>
                      </Link>
                      <IconButton
                        title="تسویه کیف پول"
                        size="small"
                        onClick={() => props.handleOpenSettleDialog(row)}
                        disabled={row.amount < 1}
                      >
                        <Icon path={mdiCurrencyUsd} size={1} />
                      </IconButton>
                      <Link to={`drivers-wallet/${row.driverId}/history`}>
                        <IconButton
                          title="نمایش صورت حساب های راننده"
                          size="small"
                        >
                          <Icon
                            color={theme.palette.blue.main}
                            path={mdiNewspaperVariantMultipleOutline}
                            size={1}
                          />
                        </IconButton>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
